import { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { feedAPI, uploadAPI, eventsAPI, jobsAPI, usersAPI, videoAPI, clipsAPI, curatedAPI, externalJobsAPI, getShareUrl } from '../lib/api';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import MentionTextarea from '../components/MentionTextarea';
import MentionText from '../components/MentionText';
import {
  Heart, MessageCircle, Share2, Image as ImageIcon, Send, X, Loader2,
  MoreHorizontal, Trash2, Flag, Edit2, Copy, Check, Calendar, Briefcase,
  MapPin, ArrowRight, TrendingUp, UserPlus, Smile, Video, Hash, Search, Filter, CalendarPlus
} from 'lucide-react';
import { Progress } from '../components/ui/progress';
import { ReportDialog } from '../components/ReportButton';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../components/ui/hover-card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { useCommunity } from '../contexts/CommunityContext';
import GifPicker from '../components/GifPicker';
import LocationAutocomplete from '../components/LocationAutocomplete';
import ClipCreator from '../components/ClipCreator';
import ClipViewer from '../components/ClipViewer';
import usePageTitle from '../hooks/usePageTitle';
import ProfilePreview from '../components/ProfilePreview';
import ReactionBar, { REACTIONS, REACTION_BY_TYPE, getMyReaction } from '../components/ReactionBar';

const FEELINGS = [
  { emoji: '😊', label: 'Happy' }, { emoji: '🎉', label: 'Celebrating' },
  { emoji: '💪', label: 'Motivated' }, { emoji: '🙏', label: 'Grateful' },
  { emoji: '😎', label: 'Confident' }, { emoji: '🤔', label: 'Thoughtful' },
  { emoji: '😤', label: 'Determined' }, { emoji: '🥳', label: 'Excited' },
  { emoji: '😴', label: 'Tired' }, { emoji: '📚', label: 'Studying' },
  { emoji: '💼', label: 'Working' }, { emoji: '✈️', label: 'Traveling' },
  { emoji: '🏋️', label: 'Working Out' }, { emoji: '🎵', label: 'Listening to Music' },
  { emoji: '🍽️', label: 'Eating' }, { emoji: '☕', label: 'Drinking Coffee' },
];

const decodeHTML = (str) => {
  if (!str) return '';
  const txt = document.createElement('textarea');
  txt.innerHTML = str;
  return txt.value;
};

const extractTikTokVideoId = (item) => {
  const m = item.link?.match(/\/video\/(\d+)/);
  return m ? m[1] : null;
};

const extractYouTubeVideoId = (item) => {
  if (item.youtube_video_id) return item.youtube_video_id;
  if (!item.link) return null;
  const m = item.link.match(/[?&]v=([\w-]+)/) || item.link.match(/youtu\.be\/([\w-]+)/);
  return m ? m[1] : null;
};

// First TikTok/YouTube video URL in a block of composer text — used to offer a
// live embed preview when a member pastes a link into the feed post box.
const FEED_VIDEO_URL_RE = /(https?:\/\/(?:www\.|vm\.|m\.)?(?:tiktok\.com|youtube\.com|youtu\.be)\/\S+)/i;

// YouTube iframe that auto-plays muted when scrolled into view, pauses when
// scrolled away. User can tap the unmute icon in YouTube's UI to enable sound.
function YouTubeVideoPlayer({ videoId }) {
  const iframeRef = useRef(null);
  useEffect(() => {
    const el = iframeRef.current;
    if (!el) return;
    const post = (func) => {
      try { el.contentWindow?.postMessage(JSON.stringify({ event: 'command', func, args: '' }), '*'); } catch {}
    };
    const observer = new IntersectionObserver(
      ([entry]) => post(entry.isIntersecting ? 'playVideo' : 'pauseVideo'),
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  if (!videoId) return null;
  const params = 'autoplay=1&mute=1&playsinline=1&enablejsapi=1&rel=0&modestbranding=1';
  return (
    <iframe
      ref={iframeRef}
      src={`https://www.youtube.com/embed/${videoId}?${params}`}
      style={{ width: '100%', aspectRatio: '16 / 9', border: 'none', borderRadius: '12px' }}
      allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
      allowFullScreen
      title={`YouTube video ${videoId}`}
    />
  );
}

// TikTok embed: starts muted via TikTok's autoplay; gets unloaded (src cleared)
// when scrolled fully out of view so multiple videos don't compete for CPU/audio.
function TikTokVideoPlayer({ videoId }) {
  const wrapperRef = useRef(null);
  const [active, setActive] = useState(false);
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setActive(entry.isIntersecting),
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  if (!videoId) return null;
  return (
    <div ref={wrapperRef}>
      {active ? (
        <iframe
          src={`https://www.tiktok.com/embed/v2/${videoId}?autoplay=1`}
          style={{ width: '100%', height: '740px', border: 'none', borderRadius: '12px' }}
          allow="autoplay; encrypted-media; fullscreen"
          sandbox="allow-scripts allow-same-origin allow-popups allow-presentation"
          allowFullScreen
          title={`TikTok video ${videoId}`}
        />
      ) : (
        <div style={{ width: '100%', height: '740px', borderRadius: '12px', background: '#1a1a1a' }} />
      )}
    </div>
  );
}

const Feed = () => {
  usePageTitle('Feed');
  const { user } = useAuth();
  const isAdmin = !!(user?.role === 'admin' || user?.roles?.includes?.('admin'));
  const navigate = useNavigate();
  const { institutionFilter, communityLabel, communityView, setCommunityView, communityOptions } = useCommunity();
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState('');
  const [linkPreview, setLinkPreview] = useState(null); // { platform, video_id, url, author, title, thumbnail, sourceUrl }
  const [linkPreviewLoading, setLinkPreviewLoading] = useState(false);
  const dismissedPreviewsRef = useRef(new Set()); // typed URLs the user closed the preview for
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [postImage, setPostImage] = useState(null);
  const [postImagePreview, setPostImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [expandedComments, setExpandedComments] = useState({});
  const [commentInputs, setCommentInputs] = useState({});
  const [submittingComment, setSubmittingComment] = useState({});
  const [commentMedia, setCommentMedia] = useState({}); // { [postId]: { type: 'image'|'gif', file?, preview?, url? } }
  const [commentGifPicker, setCommentGifPicker] = useState(null); // postId or null
  const [commentEmojiPicker, setCommentEmojiPicker] = useState(null); // postId or null
  const [editingComment, setEditingComment] = useState(null); // { postId, commentId, content }
  const [savingEdit, setSavingEdit] = useState(false);
  const [editingCurated, setEditingCurated] = useState(null); // { contentId, title, description }
  const [savingCurated, setSavingCurated] = useState(false);
  const commentFileRefs = useRef({});
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [copiedLink, setCopiedLink] = useState(null);
  const [sidebarEvents, setSidebarEvents] = useState([]);
  const [sidebarJobs, setSidebarJobs] = useState([]);
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);

  // Member search state
  const [memberSearchOpen, setMemberSearchOpen] = useState(false);
  const [memberQuery, setMemberQuery] = useState('');
  const [memberResults, setMemberResults] = useState([]);
  const [memberLoading, setMemberLoading] = useState(false);
  const memberDebounceRef = useRef(null);

  // Enhanced composer state
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [selectedGif, setSelectedGif] = useState(null);
  const [showTagPeople, setShowTagPeople] = useState(false);
  const [tagSearch, setTagSearch] = useState('');
  const [tagResults, setTagResults] = useState([]);
  const [taggedUsers, setTaggedUsers] = useState([]);
  const [showFeelings, setShowFeelings] = useState(false);
  const [selectedFeeling, setSelectedFeeling] = useState(null);
  const [showCheckIn, setShowCheckIn] = useState(false);
  const [checkInLocation, setCheckInLocation] = useState('');
  const [showTagEvent, setShowTagEvent] = useState(false);
  const [eventSearch, setEventSearch] = useState('');
  const [eventResults, setEventResults] = useState([]);
  const [taggedEvent, setTaggedEvent] = useState(null);
  const tagDebounceRef = useRef(null);

  // Clips state
  const [clipGroups, setClipGroups] = useState([]);
  const [showClipCreator, setShowClipCreator] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);
  const [viewingClips, setViewingClips] = useState(false);
  const [clipViewerStartIndex, setClipViewerStartIndex] = useState(0);

  const fetchClips = useCallback(async () => {
    try {
      const res = await clipsAPI.list(institutionFilter);
      setClipGroups(res.data || []);
    } catch { /* silent */ }
  }, [institutionFilter]);

  // Curated content
  const [curatedContent, setCuratedContent] = useState([]);
  const [curatedLikes, setCuratedLikes] = useState({}); // {content_id: {likes, liked}}
  const [curatedComments, setCuratedComments] = useState({}); // {content_id: Comment[]}
  const [curatedCommentInputs, setCuratedCommentInputs] = useState({});
  const [curatedExpandedComments, setCuratedExpandedComments] = useState({});
  const [curatedSubmitting, setCuratedSubmitting] = useState({});

  useEffect(() => {
    curatedAPI.list({ limit: 30 }).then(res => {
      const items = res.data || [];
      setCuratedContent(items);
      // Seed likes/comments state from fetched data
      const likesMap = {}, commentsMap = {};
      items.forEach(c => {
        likesMap[c.content_id] = {
          likes: c.likes || 0,
          liked: (c.liked_by || []).includes(user?.user_id),
        };
        commentsMap[c.content_id] = c.comments || [];
      });
      setCuratedLikes(likesMap);
      setCuratedComments(commentsMap);
    }).catch(() => {});
  }, [user?.user_id]);

  const handleCuratedLike = async (contentId) => {
    try {
      const res = await curatedAPI.like(contentId);
      setCuratedLikes(prev => ({ ...prev, [contentId]: { likes: res.data.likes, liked: res.data.liked } }));
    } catch { toast.error('Failed to like'); }
  };

  // Multi-reactions for curated content. Mirrors the post reaction flow:
  // optimistic update on the item, fetch reactors lazily, rollback on failure.
  const [curatedLikers, setCuratedLikers] = useState({});
  const fetchCuratedLikers = async (contentId, force = false) => {
    if (!force && curatedLikers[contentId]) return curatedLikers[contentId];
    try {
      const res = await curatedAPI.likers(contentId);
      const users = Array.isArray(res.data) ? res.data : [];
      setCuratedLikers(prev => ({ ...prev, [contentId]: users }));
      return users;
    } catch { return []; }
  };

  const applyReactionToCurated = (contentId, newType) => {
    setCuratedContent(prev => prev.map(item => {
      if (item.content_id !== contentId) return item;
      const reactions = { ...(item.reactions || {}) };
      (item.liked_by || []).forEach(uid => { if (!reactions[uid]) reactions[uid] = 'love'; });
      if (newType) reactions[user.user_id] = newType;
      else delete reactions[user.user_id];
      const love_users = Object.entries(reactions).filter(([, t]) => t === 'love').map(([uid]) => uid);
      return { ...item, reactions, liked_by: love_users, likes: Object.keys(reactions).length };
    }));
    // Sync the legacy curatedLikes map so older code keeps in step.
    setCuratedLikes(prev => ({ ...prev, [contentId]: (() => {
      const it = curatedContent.find(c => c.content_id === contentId);
      if (!it) return prev[contentId] || { likes: 0, liked: false };
      const reactions = { ...(it.reactions || {}) };
      (it.liked_by || []).forEach(uid => { if (!reactions[uid]) reactions[uid] = 'love'; });
      if (newType) reactions[user.user_id] = newType;
      else delete reactions[user.user_id];
      return {
        likes: Object.keys(reactions).length,
        liked: reactions[user.user_id] === 'love',
      };
    })() }));
    setCuratedLikers(prev => { const c = { ...prev }; delete c[contentId]; return c; });
  };

  // Reactions on comments (works for both post comments and curated-comment items).
  // Key shape: `${parentId}::${commentId}` — distinct enough since IDs come from uuid.
  const [commentLikers, setCommentLikers] = useState({});
  const commentKey = (parentId, commentId) => `${parentId}::${commentId}`;

  const fetchCommentReactors = async (parentId, commentId, kind, force = false) => {
    const k = commentKey(parentId, commentId);
    if (!force && commentLikers[k]) return commentLikers[k];
    try {
      const res = kind === 'curated'
        ? await curatedAPI.commentLikers(parentId, commentId)
        : await feedAPI.commentLikers(parentId, commentId);
      const users = Array.isArray(res.data) ? res.data : [];
      setCommentLikers(prev => ({ ...prev, [k]: users }));
      return users;
    } catch { return []; }
  };

  // Update an embedded comment's reaction fields after a successful (or
  // optimistic) react. Mutates either posts[].comments or curatedContent[].comments.
  const applyReactionToComment = (parentId, commentId, kind, newType) => {
    const updateCommentList = (list) => list.map(c => {
      if (c.comment_id !== commentId) return c;
      const reactions = { ...(c.reactions || {}) };
      (c.liked_by || []).forEach(uid => { if (!reactions[uid]) reactions[uid] = 'love'; });
      if (newType) reactions[user.user_id] = newType;
      else delete reactions[user.user_id];
      const love_users = Object.entries(reactions).filter(([, t]) => t === 'love').map(([uid]) => uid);
      return { ...c, reactions, liked_by: love_users, likes: Object.keys(reactions).length };
    });

    if (kind === 'curated') {
      setCuratedContent(prev => prev.map(item => item.content_id === parentId
        ? { ...item, comments: updateCommentList(item.comments || []) }
        : item));
      setCuratedComments(prev => ({
        ...prev,
        [parentId]: updateCommentList(prev[parentId] || []),
      }));
    } else {
      setPosts(prev => prev.map(p => p.post_id === parentId
        ? { ...p, comments: updateCommentList(p.comments || []) }
        : p));
    }
    // Invalidate the reactor cache so the next hover re-fetches.
    setCommentLikers(prev => { const c = { ...prev }; delete c[commentKey(parentId, commentId)]; return c; });
  };

  const handleReactComment = async (parentId, commentId, kind, comment, type) => {
    const prev = myReactionOn(comment);
    const newType = prev === type ? null : type;
    applyReactionToComment(parentId, commentId, kind, newType);
    try {
      if (kind === 'curated') {
        await curatedAPI.reactComment(parentId, commentId, type);
      } else {
        await feedAPI.reactComment(parentId, commentId, type);
      }
    } catch {
      applyReactionToComment(parentId, commentId, kind, prev);
      toast.error('Failed to react');
    }
  };

  const handleCuratedReact = async (contentId, type) => {
    const item = curatedContent.find(c => c.content_id === contentId);
    const prev = item ? myReactionOn(item) : null;
    const newType = prev === type ? null : type;
    applyReactionToCurated(contentId, newType);
    try {
      await curatedAPI.react(contentId, type);
    } catch {
      applyReactionToCurated(contentId, prev);
      toast.error('Failed to update reaction');
    }
  };

  const handleCuratedComment = async (contentId) => {
    const text = curatedCommentInputs[contentId]?.trim();
    if (!text) return;
    setCuratedSubmitting(prev => ({ ...prev, [contentId]: true }));
    try {
      const res = await curatedAPI.comment(contentId, text);
      setCuratedComments(prev => ({ ...prev, [contentId]: [...(prev[contentId] || []), res.data] }));
      setCuratedCommentInputs(prev => ({ ...prev, [contentId]: '' }));
    } catch { toast.error('Failed to comment'); }
    finally { setCuratedSubmitting(prev => ({ ...prev, [contentId]: false })); }
  };

  const handleCuratedShare = async (item) => {
    const url = item.link || window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied!');
    } catch { toast.error('Failed to copy link'); }
  };

  // TikTok embeds use iframes directly — no external script needed

  useEffect(() => {
    fetchPosts();
    fetchSidebarData();
    fetchClips();
  }, [institutionFilter.institution]);

  // Infinite scroll
  useEffect(() => {
    if (!loadMoreRef.current || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMore && hasMore) {
          loadMore();
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(loadMoreRef.current);
    observerRef.current = observer;
    return () => observer.disconnect();
  }, [posts.length, hasMore, loadingMore]);

  const fetchPosts = async () => {
    try {
      const res = await feedAPI.list({ limit: 20, ...institutionFilter });
      const data = Array.isArray(res.data) ? res.data : [];
      setPosts(data);
      setHasMore(data.length >= 20);
    } catch (error) {
      console.error('Failed to fetch feed:', error);
      toast.error('Failed to load feed');
    } finally {
      setLoading(false);
    }
  };

  const fetchSidebarData = async () => {
    try {
      const [eventsRes, alumniJobsRes, extJobsRes] = await Promise.allSettled([
        eventsAPI.list({ limit: 3 }),
        jobsAPI.list({ limit: 3, ...institutionFilter }),
        externalJobsAPI.list({ limit: 3 }),
      ]);
      if (eventsRes.status === 'fulfilled') setSidebarEvents((eventsRes.value.data || []).slice(0, 3));
      // Combine alumni + external jobs, take top 3
      const alumni = alumniJobsRes.status === 'fulfilled' ? (alumniJobsRes.value.data || []) : [];
      const ext = extJobsRes.status === 'fulfilled' ? (extJobsRes.value.data || []).map(j => ({ ...j, _external: true })) : [];
      const combined = [...alumni, ...ext].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)).slice(0, 3);
      setSidebarJobs(combined);
    } catch (e) { /* silent */ }
  };

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const res = await feedAPI.list({ limit: 20, skip: posts.length, ...institutionFilter });
      const data = Array.isArray(res.data) ? res.data : [];
      setPosts(prev => [...prev, ...data]);
      setHasMore(data.length >= 20);
    } catch (error) {
      console.error('Failed to load more:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Detect a pasted TikTok/YouTube link in the composer and fetch a live embed
  // preview. Debounced; skips URLs the user has explicitly dismissed.
  useEffect(() => {
    const m = newPost.match(FEED_VIDEO_URL_RE);
    const found = m ? m[1] : null;
    if (!found) { setLinkPreview(null); return; }
    if (dismissedPreviewsRef.current.has(found)) return;
    if (linkPreview?.sourceUrl === found) return; // already previewed this URL
    let cancelled = false;
    setLinkPreviewLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await feedAPI.linkPreview({ url: found });
        if (!cancelled) setLinkPreview({ ...res.data, sourceUrl: found });
      } catch {
        if (!cancelled) setLinkPreview(null);
      } finally {
        if (!cancelled) setLinkPreviewLoading(false);
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [newPost, linkPreview?.sourceUrl]);

  const dismissLinkPreview = () => {
    if (linkPreview?.sourceUrl) dismissedPreviewsRef.current.add(linkPreview.sourceUrl);
    setLinkPreview(null);
  };

  const handlePost = async () => {
    if (!newPost.trim() && !postImage && !selectedGif && !linkPreview) return;
    setPosting(true);
    try {
      let imageUrl = null;
      if (postImage) {
        setUploadingImage(true);
        const uploadRes = await uploadAPI.postImage(postImage);
        imageUrl = uploadRes.data.url;
        setUploadingImage(false);
      }
      // When a link produced an embed, drop the raw URL from the caption so the
      // player isn't shadowed by a redundant link.
      let content = newPost;
      if (linkPreview?.sourceUrl) {
        content = content.split(linkPreview.sourceUrl).join('').replace(/[ \t]{2,}/g, ' ').trim();
      }
      const payload = {
        content,
        image_url: imageUrl,
        gif_url: selectedGif || null,
        video_embed: linkPreview ? {
          platform: linkPreview.platform,
          video_id: linkPreview.video_id,
          url: linkPreview.url,
          author: linkPreview.author || '',
          title: linkPreview.title || '',
          thumbnail: linkPreview.thumbnail || '',
        } : null,
        tagged_users: taggedUsers.map(u => u.user_id),
        feeling: selectedFeeling ? `${selectedFeeling.emoji} ${selectedFeeling.label}` : null,
        location: checkInLocation || null,
        tagged_event_id: taggedEvent?.event_id || null,
        community_scope: communityView === 'gradlink' ? 'all' : (communityView === 'spelhouse' ? 'spelhouse' : user?.institution || 'all'),
        institution: user?.institution || null,
      };
      const res = await feedAPI.create(payload);
      setPosts([res.data, ...posts]);
      resetComposer();
      toast.success('Post shared!');
    } catch (error) {
      toast.error('Failed to post');
    } finally {
      setPosting(false);
      setUploadingImage(false);
    }
  };

  const resetComposer = () => {
    setNewPost('');
    setLinkPreview(null);
    dismissedPreviewsRef.current.clear();
    setPostImage(null);
    setPostImagePreview(null);
    setSelectedGif(null);
    setTaggedUsers([]);
    setSelectedFeeling(null);
    setCheckInLocation('');
    setTaggedEvent(null);
    setShowGifPicker(false);
    setShowTagPeople(false);
    setShowFeelings(false);
    setShowCheckIn(false);
    setShowTagEvent(false);
  };

  const handlePostImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Max 10MB');
      return;
    }
    setPostImage(file);
    setPostImagePreview(URL.createObjectURL(file));
    setSelectedGif(null); // Can't have both
  };

  const handleGifSelect = (url) => {
    setSelectedGif(url);
    setShowGifPicker(false);
    setPostImage(null); // Can't have both
    setPostImagePreview(null);
  };

  const handleTagSearch = (value) => {
    setTagSearch(value);
    if (tagDebounceRef.current) clearTimeout(tagDebounceRef.current);
    tagDebounceRef.current = setTimeout(async () => {
      if (value.trim().length < 2) { setTagResults([]); return; }
      try {
        const res = await usersAPI.listUsers({ search: value.trim(), limit: 5 });
        setTagResults(Array.isArray(res.data) ? res.data.filter(u => u.user_id !== user?.user_id && !taggedUsers.find(t => t.user_id === u.user_id)) : []);
      } catch { setTagResults([]); }
    }, 300);
  };

  const addTaggedUser = (u) => {
    setTaggedUsers(prev => [...prev, u]);
    setTagSearch('');
    setTagResults([]);
  };

  const removeTaggedUser = (userId) => {
    setTaggedUsers(prev => prev.filter(u => u.user_id !== userId));
  };

  const handleEventSearch = async (value) => {
    setEventSearch(value);
    if (value.trim().length < 2) { setEventResults([]); return; }
    try {
      const res = await eventsAPI.list({ limit: 5 });
      const events = Array.isArray(res.data) ? res.data : [];
      setEventResults(events.filter(e => e.title.toLowerCase().includes(value.toLowerCase())));
    } catch { setEventResults([]); }
  };

  // Cached reactors per post (for hover popover). Full list also shown in a
  // dialog when the count is clicked. Cache keyed by postId; entries include
  // each user's reaction_type for grouping in the UI.
  const [postLikers, setPostLikers] = useState({});
  const [likersDialog, setLikersDialog] = useState(null); // post object or null

  const fetchPostLikers = async (postId, force = false) => {
    if (!force && postLikers[postId]) return postLikers[postId];
    try {
      const res = await feedAPI.likers(postId);
      const users = Array.isArray(res.data) ? res.data : [];
      setPostLikers(prev => ({ ...prev, [postId]: users }));
      return users;
    } catch {
      return [];
    }
  };

  const openLikersDialog = async (item) => {
    // item may carry _kind ('curated' | 'comment') so we route to the right API.
    let users = [];
    try {
      if (item._kind === 'curated') {
        const res = await curatedAPI.likers(item.content_id);
        users = Array.isArray(res.data) ? res.data : [];
      } else if (item._kind === 'comment') {
        const res = item._sourceKind === 'curated'
          ? await curatedAPI.commentLikers(item._parentId, item.comment_id)
          : await feedAPI.commentLikers(item._parentId, item.comment_id);
        users = Array.isArray(res.data) ? res.data : [];
      } else {
        users = await fetchPostLikers(item.post_id, true);
      }
    } catch {
      users = [];
    }
    setLikersDialog({ ...item, _likers: users });
  };

  // Thin closure wrapper around the imported getMyReaction so call sites don't
  // have to repeat user.user_id everywhere.
  const myReactionOn = (item) => getMyReaction(item, user?.user_id);

  // Optimistically update local state after a react/toggle.
  const applyReactionToPost = (postId, prevType, newType) => {
    setPosts(prev => prev.map(p => {
      if (p.post_id !== postId) return p;
      const reactions = { ...(p.reactions || {}) };
      // Hydrate legacy liked_by into reactions if missing
      (p.liked_by || []).forEach(uid => { if (!reactions[uid]) reactions[uid] = 'love'; });
      if (newType) reactions[user.user_id] = newType;
      else delete reactions[user.user_id];
      const love_users = Object.entries(reactions).filter(([, t]) => t === 'love').map(([uid]) => uid);
      return {
        ...p,
        reactions,
        liked_by: love_users,
        likes: Object.keys(reactions).length,
      };
    }));
    // Invalidate the likers cache for this post so the next hover re-fetches.
    setPostLikers(prev => { const c = { ...prev }; delete c[postId]; return c; });
  };

  // React with a specific type. Posting same type toggles off; posting a
  // different type changes the reaction.
  const handleReact = async (postId, type) => {
    const post = posts.find(p => p.post_id === postId);
    const prev = post ? myReactionOn(post) : null;
    const newType = prev === type ? null : type;
    applyReactionToPost(postId, prev, newType);
    try {
      await feedAPI.react(postId, type);
    } catch {
      // Revert on failure
      applyReactionToPost(postId, newType, prev);
      toast.error('Failed to update reaction');
    }
  };

  // Back-compat: kept so existing callsites referencing handleLike (e.g. in the
  // curated content pane) keep working. Maps to a love-toggle.
  const handleLike = async (postId) => handleReact(postId, 'love');

  const handleComment = async (postId) => {
    const content = commentInputs[postId]?.trim();
    const media = commentMedia[postId];
    if (!content && !media) return;
    setSubmittingComment(prev => ({ ...prev, [postId]: true }));
    try {
      let imageUrl = null;
      let gifUrl = null;
      // Upload image if attached
      if (media?.type === 'image' && media.file) {
        const uploadRes = await uploadAPI.postImage(media.file);
        imageUrl = uploadRes.data.url;
      } else if (media?.type === 'gif' && media.url) {
        gifUrl = media.url;
      }
      const payload = { content: content || '', image_url: imageUrl, gif_url: gifUrl };
      const res = await feedAPI.comment(postId, payload);
      setPosts(posts.map(p => {
        if (p.post_id === postId) {
          const newComment = {
            user_id: user.user_id,
            user_name: user.name,
            user_photo: user.profile_photo,
            content: content || '',
            image_url: imageUrl,
            gif_url: gifUrl,
            created_at: new Date().toISOString()
          };
          return { ...p, comments: [...(p.comments || []), newComment] };
        }
        return p;
      }));
      setCommentInputs(prev => ({ ...prev, [postId]: '' }));
      setCommentMedia(prev => { const n = { ...prev }; delete n[postId]; return n; });
      setCommentGifPicker(null);
      setCommentEmojiPicker(null);
    } catch (error) {
      toast.error('Failed to comment');
    } finally {
      setSubmittingComment(prev => ({ ...prev, [postId]: false }));
    }
  };

  const handleCommentImageSelect = (postId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Max 10MB');
      return;
    }
    setCommentMedia(prev => ({
      ...prev,
      [postId]: { type: 'image', file, preview: URL.createObjectURL(file) }
    }));
    setCommentGifPicker(null);
  };

  const handleCommentGifSelect = (postId, url) => {
    setCommentMedia(prev => ({
      ...prev,
      [postId]: { type: 'gif', url }
    }));
    setCommentGifPicker(null);
  };

  const handleCommentEmojiSelect = (postId, emoji) => {
    setCommentInputs(prev => ({
      ...prev,
      [postId]: (prev[postId] || '') + emoji
    }));
    setCommentEmojiPicker(null);
  };

  const removeCommentMedia = (postId) => {
    setCommentMedia(prev => {
      const n = { ...prev };
      if (n[postId]?.preview) URL.revokeObjectURL(n[postId].preview);
      delete n[postId];
      return n;
    });
  };

  const EMOJI_LIST = ['😊','😂','❤️','🔥','👏','🎉','💯','🙌','👀','😍','🤝','💪','🙏','✨','👍','😎','🥳','💡','📌','🎯'];

  const startEditComment = (postId, comment) => {
    setEditingComment({ postId, commentId: comment.comment_id, content: comment.content || '' });
  };

  const cancelEditComment = () => {
    setEditingComment(null);
  };

  const saveEditComment = async () => {
    if (!editingComment) return;
    const { postId, commentId, content } = editingComment;
    setSavingEdit(true);
    try {
      const res = await feedAPI.editComment(postId, commentId, { content });
      setPosts(posts.map(p => {
        if (p.post_id === postId) {
          return {
            ...p,
            comments: (p.comments || []).map(c =>
              c.comment_id === commentId
                ? { ...c, content, edited_at: res.data.edited_at || new Date().toISOString() }
                : c
            )
          };
        }
        return p;
      }));
      setEditingComment(null);
      toast.success('Comment updated');
    } catch (error) {
      toast.error('Failed to update comment');
    } finally {
      setSavingEdit(false);
    }
  };

  const startEditCurated = (item) => {
    setEditingCurated({ contentId: item.content_id, title: item.title || '' });
  };

  const saveCuratedEdit = async () => {
    if (!editingCurated) return;
    const { contentId, title } = editingCurated;
    setSavingCurated(true);
    try {
      await curatedAPI.editContent(contentId, { title });
      setCuratedContent(prev => prev.map(c =>
        c.content_id === contentId ? { ...c, title } : c
      ));
      setEditingCurated(null);
      toast.success('Post updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update post');
    } finally {
      setSavingCurated(false);
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      await feedAPI.delete?.(postId) || toast.info('Delete coming soon');
      setPosts(posts.filter(p => p.post_id !== postId));
      toast.success('Post deleted');
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const handleShare = async (postId) => {
    const url = getShareUrl('post', postId);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedLink(postId);
      setTimeout(() => setCopiedLink(null), 2000);
      toast.success('Link copied!');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const toggleComments = (postId) => {
    setExpandedComments(prev => ({ ...prev, [postId]: !prev[postId] }));
  };

  const closeAllPanels = () => {
    setShowGifPicker(false);
    setShowTagPeople(false);
    setShowFeelings(false);
    setShowCheckIn(false);
    setShowTagEvent(false);
  };

  const handleMemberSearch = (value) => {
    setMemberQuery(value);
    if (memberDebounceRef.current) clearTimeout(memberDebounceRef.current);
    if (!value.trim()) { setMemberResults([]); return; }
    memberDebounceRef.current = setTimeout(async () => {
      setMemberLoading(true);
      try {
        const res = await usersAPI.listUsers({ search: value.trim(), limit: 8, ...institutionFilter });
        setMemberResults(Array.isArray(res.data) ? res.data.filter(u => u.user_id !== user?.user_id) : []);
      } catch { setMemberResults([]); }
      finally { setMemberLoading(false); }
    }, 300);
  };

  const FeedSkeleton = () => (
    <div className="space-y-4">
      {[1, 2, 3].map(i => (
        <div key={i} className="bg-white rounded-3xl p-6 border border-slate-100">
          <div className="flex items-start space-x-3 mb-4">
            <Skeleton className="w-10 h-10 rounded-full" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-20" />
            </div>
          </div>
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-4 w-3/4 mb-4" />
          <Skeleton className="h-40 w-full rounded-xl" />
        </div>
      ))}
    </div>
  );

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="flex gap-6">
      {/* Main Feed Column */}
      <div className="flex-1 max-w-2xl">
        {/* Onboarding Reminder */}
        {(user?.profile_completion || 0) < 80 && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-5 border border-amber-100 mb-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-dark-slate flex items-center">
                <TrendingUp className="w-5 h-5 mr-2 text-amber-600" />Complete Your Profile
              </h3>
              <span className="text-amber-700 font-bold text-sm">{user?.profile_completion || 0}%</span>
            </div>
            <Progress value={user?.profile_completion || 0} className="h-2 mb-3" />
            <p className="text-sm text-slate-gray mb-3">
              Complete your profile to get AI-powered mentor matches, dating recommendations, and unlock the full alumni network.
            </p>
            <Link to="/profile">
              <Button size="sm" className="rounded-full bg-amber-600 hover:bg-amber-700 text-white">
                Complete Profile <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </Link>
          </div>
        )}

        {/* Community Toggle */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex items-center bg-slate-100 rounded-full p-1 flex-1">
            {communityOptions.map(opt => (
              <button
                key={opt.value}
                onClick={() => setCommunityView(opt.value)}
                className={`flex-1 px-3 py-2 rounded-full text-xs sm:text-sm font-medium transition-colors text-center ${
                  communityView === opt.value
                    ? 'bg-white text-dark-slate shadow-sm'
                    : 'text-slate-gray hover:text-dark-slate'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div className={`rounded-2xl px-4 py-3 mb-4 border flex items-start gap-3 ${
          communityView === 'gradlink'
            ? 'bg-emerald-50 border-emerald-200'
            : 'bg-amber-50 border-amber-200'
        }`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
            communityView === 'gradlink' ? 'bg-emerald-100' : 'bg-amber-100'
          }`}>
            <Filter className={`w-4 h-4 ${communityView === 'gradlink' ? 'text-emerald-600' : 'text-amber-600'}`} />
          </div>
          <div className="flex-1 text-sm">
            {communityView === 'gradlink' ? (
              <>
                <p className="font-semibold text-emerald-900">Showing posts from all of GradLink</p>
                <p className="text-emerald-700 text-xs mt-0.5">You&rsquo;re seeing posts from alumni at every HBCU. Use the header toggle to narrow down to your school or SpelHouse.</p>
              </>
            ) : (
              <>
                <p className="font-semibold text-amber-900">Filtered to {communityView === 'spelhouse' ? 'SpelHouse' : communityLabel}</p>
                <p className="text-amber-700 text-xs mt-0.5">Posts from alumni at other schools are hidden. Switch to <span className="font-semibold">All GradLink</span> in the header to see everything.</p>
              </>
            )}
          </div>
        </div>

        {/* Clips Story Bar */}
        <div className="mb-4 -mx-4 px-4">
          <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-hide">
            {/* Create Clip Button */}
            <button
              onClick={() => setShowClipCreator(true)}
              className="flex-shrink-0 flex flex-col items-center gap-1"
            >
              <div className="w-16 h-16 rounded-full border-2 border-dashed border-mint-fresh flex items-center justify-center bg-mint-fresh/5">
                <span className="text-mint-fresh text-2xl">+</span>
              </div>
              <span className="text-[10px] text-slate-gray font-medium">Your Clip</span>
            </button>

            {/* User Clip Avatars */}
            {clipGroups.map((group, i) => (
              <button
                key={group.user_id}
                onClick={() => { setClipViewerStartIndex(i); setViewingClips(true); }}
                className="flex-shrink-0 flex flex-col items-center gap-1"
              >
                <div className="w-16 h-16 rounded-full p-[2px] bg-gradient-to-br from-mint-fresh to-emerald-400">
                  <div className="w-full h-full rounded-full border-2 border-white dark:border-dark-slate overflow-hidden bg-slate-200">
                    {group.user_photo ? (
                      <img src={group.user_photo} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-gray font-bold text-sm">
                        {group.user_name?.charAt(0)}
                      </div>
                    )}
                  </div>
                </div>
                <span className="text-[10px] text-slate-gray font-medium truncate w-16 text-center">
                  {group.user_name?.split(' ')[0]}
                </span>
              </button>
            ))}

            {clipGroups.length === 0 && (
              <div className="flex items-center gap-2 pl-2">
                <span className="text-sm text-slate-gray">No clips yet. Be the first!</span>
                <Link to="/clips" className="text-sm text-mint-fresh font-medium hover:underline">Explore Clips</Link>
              </div>
            )}
          </div>
        </div>

        {/* Clip Creator Dialog */}
        <ClipCreator open={showClipCreator} onOpenChange={setShowClipCreator} onClipCreated={fetchClips} />

        {/* Clip Viewer Overlay */}
        {viewingClips && clipGroups.length > 0 && (
          <ClipViewer
            clipGroups={clipGroups}
            initialUserIndex={clipViewerStartIndex}
            onClose={() => setViewingClips(false)}
            onClipDeleted={fetchClips}
          />
        )}

        {/* Find Members */}
        <div className="mb-4">
          {!memberSearchOpen ? (
            <button
              onClick={() => setMemberSearchOpen(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-2xl border border-slate-100 shadow-ambient hover:shadow-hover transition-shadow w-full text-left"
            >
              <Search className="w-4 h-4 text-slate-gray" />
              <span className="text-sm text-slate-gray">Find members by name, title, or company...</span>
              <UserPlus className="w-4 h-4 text-slate-gray ml-auto" />
            </button>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-ambient p-4">
              <div className="flex items-center gap-2 mb-3">
                <Search className="w-4 h-4 text-slate-gray flex-shrink-0" />
                <Input
                  placeholder="Search by name, title, or company..."
                  value={memberQuery}
                  onChange={(e) => handleMemberSearch(e.target.value)}
                  className="h-9 rounded-xl bg-slate-50 border-slate-200 text-sm"
                  autoFocus
                />
                <button onClick={() => { setMemberSearchOpen(false); setMemberQuery(''); setMemberResults([]); }}
                  className="p-1.5 rounded-full hover:bg-slate-100">
                  <X className="w-4 h-4 text-slate-gray" />
                </button>
              </div>
              {memberLoading && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-slate-gray" />
                </div>
              )}
              {!memberLoading && memberResults.length > 0 && (
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {memberResults.map(m => (
                    <Link key={m.user_id} to={`/profile/${m.user_id}`}
                      className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50 transition-colors">
                      <Avatar className="w-10 h-10 flex-shrink-0">
                        <AvatarImage src={m.profile_photo} />
                        <AvatarFallback className="bg-mint-fresh text-dark-slate text-sm">{m.name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-dark-slate truncate">{m.name}</p>
                        <p className="text-xs text-slate-gray truncate">
                          {[m.title, m.company].filter(Boolean).join(' at ') || m.institution || 'Alumni'}
                        </p>
                      </div>
                      {m.institution && (
                        <Badge variant="outline" className="text-[10px] flex-shrink-0 hidden sm:inline-flex">{m.institution}</Badge>
                      )}
                    </Link>
                  ))}
                </div>
              )}
              {!memberLoading && memberQuery.trim() && memberResults.length === 0 && (
                <p className="text-sm text-slate-gray text-center py-3">No members found</p>
              )}
            </div>
          )}
        </div>

        {/* Create Post */}
        <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-ambient mb-6">
          <div className="flex items-start space-x-4">
            <Avatar className="w-12 h-12 flex-shrink-0">
              <AvatarImage src={user?.profile_photo} />
              <AvatarFallback className="bg-mint-fresh text-dark-slate">{user?.name?.charAt(0)}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              {/* Feeling/location/tagged info strip */}
              {(selectedFeeling || checkInLocation || taggedUsers.length > 0 || taggedEvent) && (
                <div className="flex flex-wrap items-center gap-1.5 mb-2 text-xs text-slate-gray">
                  {selectedFeeling && (
                    <Badge variant="secondary" className="bg-amber-50 text-amber-700 gap-1">
                      {selectedFeeling.emoji} {selectedFeeling.label}
                      <button onClick={() => setSelectedFeeling(null)}><X className="w-3 h-3" /></button>
                    </Badge>
                  )}
                  {checkInLocation && (
                    <Badge variant="secondary" className="bg-blue-50 text-blue-700 gap-1">
                      <MapPin className="w-3 h-3" />{checkInLocation}
                      <button onClick={() => setCheckInLocation('')}><X className="w-3 h-3" /></button>
                    </Badge>
                  )}
                  {taggedUsers.map(u => (
                    <Badge key={u.user_id} variant="secondary" className="bg-indigo-50 text-indigo-700 gap-1">
                      @{u.name}
                      <button onClick={() => removeTaggedUser(u.user_id)}><X className="w-3 h-3" /></button>
                    </Badge>
                  ))}
                  {taggedEvent && (
                    <Badge variant="secondary" className="bg-purple-50 text-purple-700 gap-1">
                      <Calendar className="w-3 h-3" />{taggedEvent.title}
                      <button onClick={() => setTaggedEvent(null)}><X className="w-3 h-3" /></button>
                    </Badge>
                  )}
                </div>
              )}

              <MentionTextarea
                placeholder="What's on your mind? Type @ to mention an alum."
                value={newPost}
                onChange={setNewPost}
                className="resize-none border-0 bg-slate-50 rounded-xl focus:ring-1 focus:ring-mint-fresh"
                rows={3}
                data-testid="post-input"
              />

              {/* Image preview */}
              {postImagePreview && (
                <div className="relative mt-2 inline-block">
                  <img src={postImagePreview} alt="Preview" className="max-h-40 rounded-xl object-cover" />
                  <button onClick={() => { setPostImage(null); setPostImagePreview(null); }}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-dark-slate text-white rounded-full flex items-center justify-center">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}

              {/* GIF preview */}
              {selectedGif && (
                <div className="relative mt-2 inline-block">
                  <img src={selectedGif} alt="GIF" className="max-h-40 rounded-xl object-cover" />
                  <button onClick={() => setSelectedGif(null)}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-dark-slate text-white rounded-full flex items-center justify-center">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}

              {/* Live video-link preview */}
              {linkPreviewLoading && !linkPreview && (
                <div className="mt-2 flex items-center gap-2 text-sm text-slate-gray">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading video preview...
                </div>
              )}
              {linkPreview && (
                <div className="relative mt-2 rounded-2xl border border-slate-200 dark:border-slate-700 p-3 bg-slate-50/60 dark:bg-slate-800/40">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${linkPreview.platform === 'tiktok' ? 'text-pink-500 bg-pink-500/10' : 'text-red-600 bg-red-500/10'}`}>
                      {linkPreview.platform === 'tiktok' ? 'TikTok' : 'YouTube'}
                    </span>
                    {linkPreview.author && <span className="text-[11px] text-slate-gray truncate">{linkPreview.author}</span>}
                    <button onClick={dismissLinkPreview} title="Remove preview"
                      className="ml-auto text-slate-gray hover:text-dark-slate transition-colors">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="rounded-xl overflow-hidden">
                    {linkPreview.platform === 'tiktok'
                      ? <TikTokVideoPlayer videoId={linkPreview.video_id} />
                      : <YouTubeVideoPlayer videoId={linkPreview.video_id} />}
                  </div>
                </div>
              )}

              {/* Composer action buttons */}
              <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
                <div className="flex items-center gap-1 flex-wrap">
                  <label className="cursor-pointer">
                    <Button variant="ghost" size="sm" className="text-slate-gray h-8 px-2" asChild>
                      <span><ImageIcon className="w-4 h-4 mr-1" />Photo</span>
                    </Button>
                    <input type="file" accept="image/*" className="hidden" onChange={handlePostImageSelect} />
                  </label>
                  <Button variant="ghost" size="sm" className={`h-8 px-2 ${showTagPeople ? 'bg-indigo-50 text-indigo-700' : 'text-slate-gray'}`}
                    onClick={() => { closeAllPanels(); setShowTagPeople(!showTagPeople); }}>
                    <UserPlus className="w-4 h-4 mr-1" />Tag
                  </Button>
                  <Button variant="ghost" size="sm" className={`h-8 px-2 ${showFeelings ? 'bg-amber-50 text-amber-700' : 'text-slate-gray'}`}
                    onClick={() => { closeAllPanels(); setShowFeelings(!showFeelings); }}>
                    <Smile className="w-4 h-4 mr-1" />Feeling
                  </Button>
                  <Button variant="ghost" size="sm" className={`h-8 px-2 ${showCheckIn ? 'bg-blue-50 text-blue-700' : 'text-slate-gray'}`}
                    onClick={() => { closeAllPanels(); setShowCheckIn(!showCheckIn); }}>
                    <MapPin className="w-4 h-4 mr-1" />Check In
                  </Button>
                  <Button variant="ghost" size="sm" className={`h-8 px-2 ${showGifPicker ? 'bg-emerald-50 text-emerald-700' : 'text-slate-gray'}`}
                    onClick={() => { closeAllPanels(); setShowGifPicker(!showGifPicker); }}>
                    GIF
                  </Button>
                  <Button variant="ghost" size="sm" className={`h-8 px-2 ${showTagEvent ? 'bg-purple-50 text-purple-700' : 'text-slate-gray'}`}
                    onClick={() => { closeAllPanels(); setShowTagEvent(!showTagEvent); }}>
                    <Hash className="w-4 h-4 mr-1" />Tag Event
                  </Button>
                  <Button variant="ghost" size="sm" className="text-slate-gray h-8 px-2"
                    onClick={() => navigate('/events?create=1')}
                    title="Create a new event everyone can RSVP to">
                    <CalendarPlus className="w-4 h-4 mr-1" />Post Event
                  </Button>
                  <Button variant="ghost" size="sm" className="text-slate-gray h-8 px-2"
                    onClick={async () => {
                      try {
                        const res = await videoAPI.createRoom({ consultation_type: 'live_stream' });
                        navigate(`/video/${res.data.room_id}`);
                      } catch (e) {
                        toast.error('Failed to start live video');
                      }
                    }}>
                    <Video className="w-4 h-4 mr-1" />Live
                  </Button>
                </div>
                <Button onClick={handlePost} disabled={posting || (!newPost.trim() && !postImage && !selectedGif && !linkPreview)}
                  className="bg-dark-slate text-white rounded-full px-6" data-testid="post-submit">
                  {uploadingImage ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  {posting ? 'Posting...' : 'Post'}
                </Button>
              </div>

              {/* Tag People panel */}
              {showTagPeople && (
                <div className="mt-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
                  <Input placeholder="Search people to tag..." value={tagSearch}
                    onChange={(e) => handleTagSearch(e.target.value)}
                    className="h-8 text-sm rounded-lg mb-2" autoFocus />
                  {tagResults.length > 0 && (
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {tagResults.map(u => (
                        <button key={u.user_id} onClick={() => addTaggedUser(u)}
                          className="flex items-center gap-2 w-full p-1.5 rounded-lg hover:bg-white transition-colors text-left">
                          <Avatar className="w-6 h-6">
                            <AvatarImage src={u.profile_photo} />
                            <AvatarFallback className="bg-mint-fresh text-dark-slate text-xs">{u.name?.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <span className="text-sm text-dark-slate">{u.name}</span>
                          {u.institution && <span className="text-xs text-slate-gray">· {u.institution}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Feelings panel */}
              {showFeelings && (
                <div className="mt-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="grid grid-cols-4 gap-1.5">
                    {FEELINGS.map(f => (
                      <button key={f.label} onClick={() => { setSelectedFeeling(f); setShowFeelings(false); }}
                        className={`flex flex-col items-center p-2 rounded-lg hover:bg-white transition-colors text-center ${
                          selectedFeeling?.label === f.label ? 'bg-white ring-1 ring-amber-300' : ''
                        }`}>
                        <span className="text-lg">{f.emoji}</span>
                        <span className="text-[10px] text-slate-gray mt-0.5">{f.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Check In panel */}
              {showCheckIn && (
                <div className="mt-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
                  <LocationAutocomplete
                    value={checkInLocation}
                    onChange={setCheckInLocation}
                    placeholder="Enter your location..."
                    className="h-8 text-sm rounded-lg"
                  />
                  {checkInLocation && (
                    <Button size="sm" className="mt-2 h-7 text-xs rounded-full" onClick={() => setShowCheckIn(false)}>
                      <Check className="w-3 h-3 mr-1" />Done
                    </Button>
                  )}
                </div>
              )}

              {/* GIF picker */}
              {showGifPicker && (
                <div className="mt-3">
                  <GifPicker onSelect={handleGifSelect} onClose={() => setShowGifPicker(false)} />
                </div>
              )}

              {/* Tag Event panel */}
              {showTagEvent && (
                <div className="mt-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
                  <Input placeholder="Search events..." value={eventSearch}
                    onChange={(e) => handleEventSearch(e.target.value)}
                    className="h-8 text-sm rounded-lg mb-2" autoFocus />
                  {eventResults.length > 0 && (
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {eventResults.map(e => (
                        <button key={e.event_id} onClick={() => { setTaggedEvent(e); setShowTagEvent(false); setEventSearch(''); }}
                          className="flex items-center gap-2 w-full p-1.5 rounded-lg hover:bg-white transition-colors text-left">
                          <Calendar className="w-4 h-4 text-purple-600 flex-shrink-0" />
                          <div>
                            <span className="text-sm text-dark-slate">{e.title}</span>
                            <span className="text-xs text-slate-gray ml-2">{e.date}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Posts */}
        <div className="space-y-4">
          {loading ? <FeedSkeleton /> : (
            <>
            {(() => {
              // Build unified timeline: interleave posts, tiktoks, and articles so no type clumps

              // Dedupe curated by link
              const seenLinks = new Set();
              const dedupedCurated = curatedContent.filter(c => {
                if (seenLinks.has(c.link)) return false;
                seenLinks.add(c.link);
                return true;
              });

              // Manually-pasted videos (pinned_to_feed) behave like normal posts:
              // they join the post stream and surface by recency — a just-posted
              // one lands at the top — instead of being folded into the creator
              // round-robin interleave below.
              const pinnedVideos = dedupedCurated.filter(c => c.pinned_to_feed).map(c => ({ ...c, _type: c.content_type }));
              const userPosts = [...posts.map(p => ({ ...p, _type: 'post' })), ...pinnedVideos]
                .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

              // Separate tiktoks, youtubes, and articles (excluding pinned, handled above)
              const tiktoksRaw = dedupedCurated.filter(c => c.content_type === 'tiktok' && !c.pinned_to_feed).map(c => ({ ...c, _type: 'tiktok' }));
              const youtubesRaw = dedupedCurated.filter(c => c.content_type === 'youtube' && !c.pinned_to_feed).map(c => ({ ...c, _type: 'youtube' }));
              const articles = dedupedCurated.filter(c => c.content_type !== 'tiktok' && c.content_type !== 'youtube' && !c.pinned_to_feed).map(c => ({ ...c, _type: 'article' }));

              // Round-robin by creator so two consecutive video cards from the
              // same person never appear back-to-back. Group by author, then
              // pull one from each group in turn until all are exhausted.
              const roundRobinByCreator = (items, creatorOf) => {
                const groups = new Map();
                items.forEach(it => {
                  const k = (creatorOf(it) || 'unknown').toLowerCase();
                  if (!groups.has(k)) groups.set(k, []);
                  groups.get(k).push(it);
                });
                const out = [];
                let i = 0;
                let added = true;
                while (added) {
                  added = false;
                  for (const list of groups.values()) {
                    if (list.length > i) { out.push(list[i]); added = true; }
                  }
                  i += 1;
                }
                return out;
              };
              const tiktoks = roundRobinByCreator(tiktoksRaw, t => t.tiktok_author || t.source);
              const youtubes = roundRobinByCreator(youtubesRaw, y => y.youtube_channel_id || y.youtube_author || y.source);

              // Round-robin interleave all four types for maximum variety
              const timeline = [];
              let pIdx = 0, tIdx = 0, yIdx = 0, aIdx = 0;
              const sources = ['post', 'article', 'youtube', 'post', 'tiktok', 'post', 'article', 'youtube']; // pattern per cycle
              let sIdx = 0;
              while (pIdx < userPosts.length || tIdx < tiktoks.length || yIdx < youtubes.length || aIdx < articles.length) {
                const type = sources[sIdx % sources.length];
                sIdx++;
                if (type === 'post' && pIdx < userPosts.length) { timeline.push(userPosts[pIdx++]); }
                else if (type === 'tiktok' && tIdx < tiktoks.length) { timeline.push(tiktoks[tIdx++]); }
                else if (type === 'youtube' && yIdx < youtubes.length) { timeline.push(youtubes[yIdx++]); }
                else if (type === 'article' && aIdx < articles.length) { timeline.push(articles[aIdx++]); }
                else {
                  // Current type exhausted, grab next available
                  if (pIdx < userPosts.length) timeline.push(userPosts[pIdx++]);
                  else if (aIdx < articles.length) timeline.push(articles[aIdx++]);
                  else if (yIdx < youtubes.length) timeline.push(youtubes[yIdx++]);
                  else if (tIdx < tiktoks.length) timeline.push(tiktoks[tIdx++]);
                }
              }

              if (timeline.length === 0) return (
                <div className="bg-white rounded-3xl p-8 text-center border border-slate-100">
                  <MessageCircle className="w-12 h-12 text-slate-gray mx-auto mb-3" />
                  <p className="text-slate-gray">No posts yet. Be the first to share!</p>
                </div>
              );

              return timeline.map(item => {
                if (item._type === 'tiktok') {
                  const cLikes = curatedLikes[item.content_id] || { likes: item.likes || 0, liked: false };
                  const cComments = curatedComments[item.content_id] || [];
                  const cExpanded = curatedExpandedComments[item.content_id];
                  return (
                    <div key={`tt-${item.content_id}`} className="bg-gradient-to-r from-slate-50 to-pink-50/30 dark:from-slate-800 dark:to-pink-900/10 rounded-3xl p-4 border border-pink-200/30 shadow-ambient">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-pink-500 bg-pink-500/10 px-2 py-0.5 rounded-full">TikTok</span>
                        <span className="text-[10px] text-slate-gray">{item.tiktok_author || item.source}</span>
                        {isAdmin && editingCurated?.contentId !== item.content_id && (
                          <button onClick={() => startEditCurated(item)} title="Edit caption"
                            className="ml-auto text-slate-gray hover:text-dark-slate transition-colors">
                            <Edit2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      {editingCurated?.contentId === item.content_id ? (
                        <div className="mb-3 space-y-2">
                          <MentionTextarea
                            value={editingCurated.title}
                            onChange={(val) => setEditingCurated(prev => ({ ...prev, title: val }))}
                            placeholder="Caption — type @ to tag a GradLink member"
                            rows={3}
                            className="w-full rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm p-2 text-dark-slate dark:text-frost-white resize-y"
                          />
                          <p className="text-[11px] text-slate-gray">Type <span className="font-semibold">@</span> to tag a member — their name links to their GradLink profile.</p>
                          <div className="flex items-center gap-2">
                            <Button size="sm" onClick={saveCuratedEdit} disabled={savingCurated}
                              className="rounded-full bg-dark-slate text-white hover:bg-dark-slate/90">
                              {savingCurated ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingCurated(null)} disabled={savingCurated}
                              className="rounded-full">Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        item.title && (
                          <h3 className="font-semibold text-dark-slate dark:text-frost-white text-sm mb-3">
                            <MentionText text={decodeHTML(item.title)} />
                          </h3>
                        )
                      )}
                      <div className="rounded-xl overflow-hidden"><TikTokVideoPlayer videoId={extractTikTokVideoId(item)} /></div>
                      {/* Interaction bar */}
                      <div className="flex items-center space-x-6 pt-3 mt-3 border-t border-pink-100/60">
                        <ReactionBar
                          myReaction={myReactionOn(item)}
                          totalCount={cLikes.likes}
                          reactors={curatedLikers[item.content_id]}
                          onReact={(t) => handleCuratedReact(item.content_id, t)}
                          onShowAll={() => openLikersDialog({ ...item, _kind: 'curated' })}
                          onHoverOpen={() => fetchCuratedLikers(item.content_id)}
                        />
                        <button onClick={() => setCuratedExpandedComments(prev => ({ ...prev, [item.content_id]: !prev[item.content_id] }))}
                          className={`flex items-center space-x-2 transition-colors ${cExpanded ? 'text-dark-slate' : 'text-slate-gray hover:text-dark-slate'}`}>
                          <MessageCircle className="w-5 h-5" />
                          <span className="text-sm">{cComments.length}</span>
                        </button>
                        <button onClick={() => handleCuratedShare(item)} className="flex items-center space-x-2 text-slate-gray hover:text-dark-slate transition-colors">
                          <Share2 className="w-5 h-5" />
                        </button>
                      </div>
                      {cExpanded && (
                        <div className="mt-3 pt-3 border-t border-pink-100/60 space-y-3">
                          {cComments.map((c, i) => (
                            <div key={c.comment_id || i} className="flex items-start space-x-2">
                              <Avatar className="w-7 h-7 flex-shrink-0">
                                <AvatarImage src={c.user_photo} />
                                <AvatarFallback className="bg-mint-fresh text-dark-slate text-xs">{c.user_name?.charAt(0)}</AvatarFallback>
                              </Avatar>
                              <div className="bg-slate-50 rounded-xl px-3 py-2 flex-1">
                                <p className="text-xs font-semibold text-dark-slate">{c.user_name}</p>
                                <p className="text-sm text-dark-slate">{c.content}</p>
                                <div className="mt-1">
                                  <ReactionBar
                                    size="sm"
                                    myReaction={myReactionOn(c)}
                                    totalCount={c.likes || 0}
                                    reactors={commentLikers[commentKey(item.content_id, c.comment_id)]}
                                    onReact={(t) => handleReactComment(item.content_id, c.comment_id, 'curated', c, t)}
                                    onShowAll={() => openLikersDialog({ ...c, _kind: 'comment', _sourceKind: 'curated', _parentId: item.content_id })}
                                    onHoverOpen={() => fetchCommentReactors(item.content_id, c.comment_id, 'curated')}
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                          <div className="flex items-center space-x-2">
                            <Avatar className="w-8 h-8 flex-shrink-0">
                              <AvatarImage src={user?.profile_photo} />
                              <AvatarFallback className="bg-mint-fresh text-dark-slate text-xs">{user?.name?.charAt(0)}</AvatarFallback>
                            </Avatar>
                            <Input placeholder="Write a comment..." value={curatedCommentInputs[item.content_id] || ''}
                              onChange={(e) => setCuratedCommentInputs(prev => ({ ...prev, [item.content_id]: e.target.value }))}
                              onKeyDown={(e) => { if (e.key === 'Enter') handleCuratedComment(item.content_id); }}
                              className="h-9 rounded-full bg-slate-50 border-slate-200 text-sm flex-1" />
                            <Button size="sm" variant="ghost" onClick={() => handleCuratedComment(item.content_id)}
                              disabled={!curatedCommentInputs[item.content_id]?.trim() || curatedSubmitting[item.content_id]}
                              className="rounded-full p-2">
                              {curatedSubmitting[item.content_id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }

                if (item._type === 'youtube') {
                  const cLikes = curatedLikes[item.content_id] || { likes: item.likes || 0, liked: false };
                  const cComments = curatedComments[item.content_id] || [];
                  const cExpanded = curatedExpandedComments[item.content_id];
                  return (
                    <div key={`yt-${item.content_id}`} className="bg-gradient-to-r from-slate-50 to-red-50/30 dark:from-slate-800 dark:to-red-900/10 rounded-3xl p-4 border border-red-200/30 shadow-ambient">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-red-600 bg-red-500/10 px-2 py-0.5 rounded-full">YouTube</span>
                        <span className="text-[10px] text-slate-gray">{item.youtube_author || item.source}</span>
                      </div>
                      <h3 className="font-semibold text-dark-slate dark:text-frost-white text-sm line-clamp-2 mb-3">{decodeHTML(item.title)}</h3>
                      <div className="rounded-xl overflow-hidden"><YouTubeVideoPlayer videoId={extractYouTubeVideoId(item)} /></div>
                      <div className="flex items-center space-x-6 pt-3 mt-3 border-t border-red-100/60">
                        <ReactionBar
                          myReaction={myReactionOn(item)}
                          totalCount={cLikes.likes}
                          reactors={curatedLikers[item.content_id]}
                          onReact={(t) => handleCuratedReact(item.content_id, t)}
                          onShowAll={() => openLikersDialog({ ...item, _kind: 'curated' })}
                          onHoverOpen={() => fetchCuratedLikers(item.content_id)}
                        />
                        <button onClick={() => setCuratedExpandedComments(prev => ({ ...prev, [item.content_id]: !prev[item.content_id] }))}
                          className={`flex items-center space-x-2 transition-colors ${cExpanded ? 'text-dark-slate' : 'text-slate-gray hover:text-dark-slate'}`}>
                          <MessageCircle className="w-5 h-5" />
                          <span className="text-sm">{cComments.length}</span>
                        </button>
                        <button onClick={() => handleCuratedShare(item)} className="flex items-center space-x-2 text-slate-gray hover:text-dark-slate transition-colors">
                          <Share2 className="w-5 h-5" />
                        </button>
                      </div>
                      {cExpanded && (
                        <div className="mt-3 pt-3 border-t border-red-100/60 space-y-3">
                          {cComments.map((c, i) => (
                            <div key={c.comment_id || i} className="flex items-start space-x-2">
                              <Avatar className="w-7 h-7 flex-shrink-0">
                                <AvatarImage src={c.user_photo} />
                                <AvatarFallback className="bg-mint-fresh text-dark-slate text-xs">{c.user_name?.charAt(0)}</AvatarFallback>
                              </Avatar>
                              <div className="bg-slate-50 rounded-xl px-3 py-2 flex-1">
                                <p className="text-xs font-semibold text-dark-slate">{c.user_name}</p>
                                <p className="text-sm text-dark-slate">{c.content}</p>
                              </div>
                            </div>
                          ))}
                          <div className="flex items-center space-x-2">
                            <Avatar className="w-8 h-8 flex-shrink-0">
                              <AvatarImage src={user?.profile_photo} />
                              <AvatarFallback className="bg-mint-fresh text-dark-slate text-xs">{user?.name?.charAt(0)}</AvatarFallback>
                            </Avatar>
                            <Input placeholder="Write a comment..." value={curatedCommentInputs[item.content_id] || ''}
                              onChange={(e) => setCuratedCommentInputs(prev => ({ ...prev, [item.content_id]: e.target.value }))}
                              onKeyDown={(e) => { if (e.key === 'Enter') handleCuratedComment(item.content_id); }}
                              className="h-9 rounded-full bg-slate-50 border-slate-200 text-sm flex-1" />
                            <Button size="sm" variant="ghost" onClick={() => handleCuratedComment(item.content_id)}
                              disabled={!curatedCommentInputs[item.content_id]?.trim() || curatedSubmitting[item.content_id]}
                              className="rounded-full p-2">
                              {curatedSubmitting[item.content_id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }

                if (item._type === 'article') {
                  const cLikes = curatedLikes[item.content_id] || { likes: item.likes || 0, liked: false };
                  const cComments = curatedComments[item.content_id] || [];
                  const cExpanded = curatedExpandedComments[item.content_id];
                  return (
                    <div key={`art-${item.content_id}`} className="bg-white dark:bg-slate-800 rounded-3xl border border-mint-fresh/20 shadow-ambient overflow-hidden">
                      <a href={item.link} target="_blank" rel="noopener noreferrer" className="block hover:opacity-95 transition-opacity">
                        {item.image_url && (
                          <div className="relative w-full h-44 bg-slate-100">
                            <img src={item.image_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.parentElement.style.display = 'none'; }} />
                            <div className="absolute top-3 left-3">
                              <span className="text-[10px] font-semibold uppercase tracking-wider text-white bg-mint-fresh/90 backdrop-blur-sm px-2.5 py-1 rounded-full shadow-sm">Curated</span>
                            </div>
                          </div>
                        )}
                        <div className="p-5 pb-3">
                          {!item.image_url && (
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-[10px] font-semibold uppercase tracking-wider text-mint-fresh bg-mint-fresh/10 px-2 py-0.5 rounded-full">Curated</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2 mb-1.5">
                            <span className="text-[10px] text-slate-gray font-medium">{item.source}</span>
                          </div>
                          <h3 className="font-heading font-semibold text-dark-slate dark:text-frost-white text-[15px] leading-snug line-clamp-2">{decodeHTML(item.title)}</h3>
                          {item.description && <p className="text-xs text-slate-gray mt-1.5 line-clamp-2 leading-relaxed">{decodeHTML(item.description.replace(/<[^>]*>/g, ''))}</p>}
                        </div>
                      </a>
                      {/* Interaction bar */}
                      <div className="px-5 pb-4">
                        <div className="flex items-center space-x-6 pt-3 border-t border-slate-100">
                          <ReactionBar
                            myReaction={myReactionOn(item)}
                            totalCount={cLikes.likes}
                            reactors={curatedLikers[item.content_id]}
                            onReact={(t) => handleCuratedReact(item.content_id, t)}
                            onShowAll={() => openLikersDialog({ ...item, _kind: 'curated' })}
                            onHoverOpen={() => fetchCuratedLikers(item.content_id)}
                          />
                          <button onClick={() => setCuratedExpandedComments(prev => ({ ...prev, [item.content_id]: !prev[item.content_id] }))}
                            className={`flex items-center space-x-2 transition-colors ${cExpanded ? 'text-dark-slate' : 'text-slate-gray hover:text-dark-slate'}`}>
                            <MessageCircle className="w-5 h-5" />
                            <span className="text-sm">{cComments.length}</span>
                          </button>
                          <button onClick={() => handleCuratedShare(item)} className="flex items-center space-x-2 text-slate-gray hover:text-dark-slate transition-colors">
                            <Share2 className="w-5 h-5" />
                          </button>
                        </div>
                        {cExpanded && (
                          <div className="mt-3 space-y-3">
                            {cComments.map((c, i) => (
                              <div key={c.comment_id || i} className="flex items-start space-x-2">
                                <Avatar className="w-7 h-7 flex-shrink-0">
                                  <AvatarImage src={c.user_photo} />
                                  <AvatarFallback className="bg-mint-fresh text-dark-slate text-xs">{c.user_name?.charAt(0)}</AvatarFallback>
                                </Avatar>
                                <div className="bg-slate-50 rounded-xl px-3 py-2 flex-1">
                                  <p className="text-xs font-semibold text-dark-slate">{c.user_name}</p>
                                  <p className="text-sm text-dark-slate">{c.content}</p>
                                  <div className="mt-1">
                                    <ReactionBar
                                      size="sm"
                                      myReaction={myReactionOn(c)}
                                      totalCount={c.likes || 0}
                                      reactors={commentLikers[commentKey(item.content_id, c.comment_id)]}
                                      onReact={(t) => handleReactComment(item.content_id, c.comment_id, 'curated', c, t)}
                                      onShowAll={() => openLikersDialog({ ...c, _kind: 'comment', _sourceKind: 'curated', _parentId: item.content_id })}
                                      onHoverOpen={() => fetchCommentReactors(item.content_id, c.comment_id, 'curated')}
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                            <div className="flex items-center space-x-2">
                              <Avatar className="w-8 h-8 flex-shrink-0">
                                <AvatarImage src={user?.profile_photo} />
                                <AvatarFallback className="bg-mint-fresh text-dark-slate text-xs">{user?.name?.charAt(0)}</AvatarFallback>
                              </Avatar>
                              <Input placeholder="Write a comment..." value={curatedCommentInputs[item.content_id] || ''}
                                onChange={(e) => setCuratedCommentInputs(prev => ({ ...prev, [item.content_id]: e.target.value }))}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleCuratedComment(item.content_id); }}
                                className="h-9 rounded-full bg-slate-50 border-slate-200 text-sm flex-1" />
                              <Button size="sm" variant="ghost" onClick={() => handleCuratedComment(item.content_id)}
                                disabled={!curatedCommentInputs[item.content_id]?.trim() || curatedSubmitting[item.content_id]}
                                className="rounded-full p-2">
                                {curatedSubmitting[item.content_id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }

                // User post
                const post = item;
                return (
              <div key={post.post_id} className="bg-white rounded-3xl p-6 border border-slate-100 shadow-ambient" data-testid={`post-${post.post_id}`}>
                {/* Post header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start space-x-3">
                    <ProfilePreview userId={post.user_id} userName={post.user_name} userPhoto={post.user_photo}>
                      <Avatar className="w-10 h-10 hover:ring-2 hover:ring-mint-fresh/50 transition-all">
                        <AvatarImage src={post.user_photo} />
                        <AvatarFallback className="bg-mint-fresh text-dark-slate">{post.user_name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                    </ProfilePreview>
                    <div>
                      <div className="flex items-center flex-wrap gap-1">
                        <ProfilePreview userId={post.user_id} userName={post.user_name} userPhoto={post.user_photo}>
                          <p className="font-semibold text-dark-slate hover:text-mint-fresh transition-colors">{post.user_name}</p>
                        </ProfilePreview>
                        {post.feeling && <span className="text-xs text-slate-gray">is feeling {post.feeling}</span>}
                        {post.location && <span className="text-xs text-slate-gray flex items-center"><MapPin className="w-3 h-3 mr-0.5" />at {post.location}</span>}
                      </div>
                      <p className="text-xs text-slate-gray">
                        {post.created_at ? formatDistanceToNow(new Date(post.created_at), { addSuffix: true }) : ''}
                      </p>
                    </div>
                  </div>
                  {/* Post actions menu */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="p-1 rounded-full hover:bg-slate-100"><MoreHorizontal className="w-5 h-5 text-slate-gray" /></button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleShare(post.post_id)}>
                        <Copy className="w-4 h-4 mr-2" />Copy Link
                      </DropdownMenuItem>
                      {post.user_id === user?.user_id && (
                        <DropdownMenuItem onClick={() => handleDeletePost(post.post_id)} className="text-red-600">
                          <Trash2 className="w-4 h-4 mr-2" />Delete
                        </DropdownMenuItem>
                      )}
                      {post.user_id !== user?.user_id && (
                        <DropdownMenuItem onClick={() => setReportTarget({ contentType: 'post', contentId: post.post_id, authorId: post.user_id })} className="text-red-600">
                          <Flag className="w-4 h-4 mr-2" />Report
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Tagged users */}
                {post.tagged_users?.length > 0 && (
                  <div className="flex items-center gap-1 mb-2 text-xs text-slate-gray">
                    <UserPlus className="w-3 h-3" />
                    <span>with {post.tagged_users.length} {post.tagged_users.length === 1 ? 'person' : 'people'}</span>
                  </div>
                )}

                {/* Post content */}
                {post.content?.trim() && <p className="text-dark-slate mb-4"><MentionText text={post.content} /></p>}
                {post.video_embed?.video_id && (
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${post.video_embed.platform === 'tiktok' ? 'text-pink-500 bg-pink-500/10' : 'text-red-600 bg-red-500/10'}`}>
                        {post.video_embed.platform === 'tiktok' ? 'TikTok' : 'YouTube'}
                      </span>
                      {post.video_embed.author && <span className="text-[11px] text-slate-gray truncate">{post.video_embed.author}</span>}
                    </div>
                    <div className="rounded-xl overflow-hidden">
                      {post.video_embed.platform === 'tiktok'
                        ? <TikTokVideoPlayer videoId={post.video_embed.video_id} />
                        : <YouTubeVideoPlayer videoId={post.video_embed.video_id} />}
                    </div>
                  </div>
                )}
                {post.image_url && (
                  <img src={post.image_url} alt="Post" className="rounded-xl mb-4 w-full object-cover max-h-96" />
                )}
                {post.gif_url && !post.image_url && (
                  <img src={post.gif_url} alt="GIF" className="rounded-xl mb-4 max-h-72 object-cover" />
                )}

                {/* Actions bar */}
                <div className="flex items-center space-x-6 pt-4 border-t border-slate-100">
                  <ReactionBar
                    myReaction={myReactionOn(post)}
                    totalCount={post.likes || 0}
                    reactors={postLikers[post.post_id]}
                    onReact={(type) => handleReact(post.post_id, type)}
                    onShowAll={() => openLikersDialog(post)}
                    onHoverOpen={() => fetchPostLikers(post.post_id)}
                  />
                  <button onClick={() => toggleComments(post.post_id)}
                    className={`flex items-center space-x-2 transition-colors ${expandedComments[post.post_id] ? 'text-dark-slate' : 'text-slate-gray hover:text-dark-slate'}`}>
                    <MessageCircle className="w-5 h-5" />
                    <span className="text-sm">{post.comments?.length || 0}</span>
                  </button>
                  <button onClick={() => handleShare(post.post_id)}
                    className="flex items-center space-x-2 text-slate-gray hover:text-dark-slate transition-colors">
                    {copiedLink === post.post_id ? <Check className="w-5 h-5 text-emerald-500" /> : <Share2 className="w-5 h-5" />}
                  </button>
                </div>

                {/* Comments section */}
                {expandedComments[post.post_id] && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    {(post.comments || []).length > 0 && (
                      <div className="space-y-3 mb-4">
                        {(post.comments || []).map((comment, i) => (
                          <div key={i} className="flex items-start space-x-2">
                            <ProfilePreview userId={comment.user_id} userName={comment.user_name} userPhoto={comment.user_photo}>
                              <Avatar className="w-7 h-7 flex-shrink-0 hover:ring-2 hover:ring-mint-fresh/50 transition-all">
                                <AvatarImage src={comment.user_photo} />
                                <AvatarFallback className="bg-mint-fresh text-dark-slate text-xs">{comment.user_name?.charAt(0)}</AvatarFallback>
                              </Avatar>
                            </ProfilePreview>
                            <div className="bg-slate-50 rounded-xl px-3 py-2 flex-1 group/comment">
                              <div className="flex items-start justify-between">
                                <ProfilePreview userId={comment.user_id} userName={comment.user_name} userPhoto={comment.user_photo}>
                                  <p className="text-xs font-semibold text-dark-slate hover:text-mint-fresh transition-colors">{comment.user_name}</p>
                                </ProfilePreview>
                                {comment.user_id === user?.user_id && comment.comment_id && editingComment?.commentId !== comment.comment_id && (
                                  <button
                                    onClick={() => startEditComment(post.post_id, comment)}
                                    className="opacity-0 group-hover/comment:opacity-100 p-0.5 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-all"
                                    title="Edit comment"
                                  >
                                    <Edit2 className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                              {/* Edit mode */}
                              {editingComment?.commentId === comment.comment_id ? (
                                <div className="mt-1">
                                  <Input
                                    value={editingComment.content}
                                    onChange={(e) => setEditingComment(prev => ({ ...prev, content: e.target.value }))}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveEditComment(); }
                                      if (e.key === 'Escape') cancelEditComment();
                                    }}
                                    className="h-8 text-sm rounded-lg bg-white border-slate-300"
                                    autoFocus
                                  />
                                  <div className="flex items-center gap-2 mt-1.5">
                                    <Button size="sm" onClick={saveEditComment} disabled={savingEdit || !editingComment.content?.trim()}
                                      className="h-6 px-2.5 text-[11px] rounded-full bg-dark-slate text-white">
                                      {savingEdit ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                                    </Button>
                                    <button onClick={cancelEditComment} className="text-[11px] text-slate-gray hover:text-dark-slate transition-colors">
                                      Cancel
                                    </button>
                                    <span className="text-[10px] text-slate-400 ml-auto">Esc to cancel · Enter to save</span>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  {comment.content && <p className="text-sm text-dark-slate"><MentionText text={comment.content} /></p>}
                                  {comment.image_url && (
                                    <img src={comment.image_url} alt="Comment" className="rounded-lg mt-1.5 max-h-48 object-cover cursor-pointer hover:opacity-90 transition-opacity" onClick={() => window.open(comment.image_url, '_blank')} />
                                  )}
                                  {comment.gif_url && !comment.image_url && (
                                    <img src={comment.gif_url} alt="GIF" className="rounded-lg mt-1.5 max-h-36 object-cover" />
                                  )}
                                </>
                              )}
                              <div className="flex items-center gap-3 mt-1">
                                <ReactionBar
                                  size="sm"
                                  myReaction={myReactionOn(comment)}
                                  totalCount={comment.likes || 0}
                                  reactors={commentLikers[commentKey(post.post_id, comment.comment_id)]}
                                  onReact={(t) => handleReactComment(post.post_id, comment.comment_id, 'post', comment, t)}
                                  onShowAll={() => openLikersDialog({ ...comment, _kind: 'comment', _sourceKind: 'post', _parentId: post.post_id })}
                                  onHoverOpen={() => fetchCommentReactors(post.post_id, comment.comment_id, 'post')}
                                />
                                {comment.created_at && (
                                  <p className="text-[10px] text-slate-gray">
                                    {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                                    {comment.edited_at && <span className="ml-1 italic">(edited)</span>}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Comment input */}
                    <div className="space-y-2">
                      {/* Media preview */}
                      {commentMedia[post.post_id] && (
                        <div className="relative inline-block ml-10">
                          <img
                            src={commentMedia[post.post_id].preview || commentMedia[post.post_id].url}
                            alt="Attachment"
                            className="max-h-32 rounded-lg border border-slate-200 object-cover"
                          />
                          <button
                            onClick={() => removeCommentMedia(post.post_id)}
                            className="absolute -top-1.5 -right-1.5 bg-dark-slate text-white rounded-full p-0.5 hover:bg-red-500 transition-colors shadow-sm"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      )}

                      <div className="flex items-start space-x-2">
                        <Avatar className="w-8 h-8 flex-shrink-0 mt-0.5">
                          <AvatarImage src={user?.profile_photo} />
                          <AvatarFallback className="bg-mint-fresh text-dark-slate text-xs">{user?.name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex items-center space-x-1.5">
                            <MentionTextarea
                              placeholder="Write a comment... type @ to mention"
                              value={commentInputs[post.post_id] || ''}
                              onChange={(v) => setCommentInputs(prev => ({ ...prev, [post.post_id]: v }))}
                              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleComment(post.post_id); } }}
                              rows={1}
                              className="rounded-full bg-slate-50 border-slate-200 text-sm flex-1 min-h-[36px] py-2"
                            />
                            <Button size="sm" variant="ghost" onClick={() => handleComment(post.post_id)}
                              disabled={(!commentInputs[post.post_id]?.trim() && !commentMedia[post.post_id]) || submittingComment[post.post_id]}
                              className="rounded-full p-2 flex-shrink-0">
                              {submittingComment[post.post_id]
                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                : <Send className="w-4 h-4" />}
                            </Button>
                          </div>

                          {/* Comment action buttons */}
                          <div className="flex items-center gap-0.5 mt-1 ml-1">
                            {/* Photo/Video upload */}
                            <label className="cursor-pointer">
                              <button type="button" className="p-1 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                                onClick={() => commentFileRefs.current[post.post_id]?.click()}>
                                <ImageIcon className="w-3.5 h-3.5" />
                              </button>
                              <input
                                type="file"
                                accept="image/*,video/*"
                                className="hidden"
                                ref={(el) => { commentFileRefs.current[post.post_id] = el; }}
                                onChange={(e) => handleCommentImageSelect(post.post_id, e)}
                              />
                            </label>

                            {/* Emoji picker toggle */}
                            <button type="button"
                              className={`p-1 rounded-full transition-colors ${commentEmojiPicker === post.post_id ? 'bg-amber-50 text-amber-600' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'}`}
                              onClick={() => setCommentEmojiPicker(commentEmojiPicker === post.post_id ? null : post.post_id)}>
                              <Smile className="w-3.5 h-3.5" />
                            </button>

                            {/* GIF picker toggle */}
                            <button type="button"
                              className={`p-1 rounded-full text-[10px] font-bold transition-colors ${commentGifPicker === post.post_id ? 'bg-emerald-50 text-emerald-600' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'}`}
                              onClick={() => setCommentGifPicker(commentGifPicker === post.post_id ? null : post.post_id)}>
                              GIF
                            </button>
                          </div>

                          {/* Emoji picker panel */}
                          {commentEmojiPicker === post.post_id && (
                            <div className="mt-1.5 p-2 bg-white rounded-xl border border-slate-200 shadow-lg grid grid-cols-10 gap-1 w-fit">
                              {EMOJI_LIST.map((emoji) => (
                                <button key={emoji} type="button"
                                  onClick={() => handleCommentEmojiSelect(post.post_id, emoji)}
                                  className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-slate-100 text-base transition-colors">
                                  {emoji}
                                </button>
                              ))}
                            </div>
                          )}

                          {/* GIF picker panel */}
                          {commentGifPicker === post.post_id && (
                            <div className="mt-1.5">
                              <GifPicker
                                onSelect={(url) => handleCommentGifSelect(post.post_id, url)}
                                onClose={() => setCommentGifPicker(null)}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
              });
            })()}

          {/* Load more / infinite scroll trigger */}
          {hasMore && posts.length > 0 && (
            <div ref={loadMoreRef} className="py-4 text-center">
              {loadingMore && (
                <Loader2 className="w-6 h-6 animate-spin text-slate-gray mx-auto" />
              )}
            </div>
          )}
          </>
          )}
        </div>
      </div>

      {/* Right Sidebar */}
      <aside className="hidden lg:block w-80 flex-shrink-0 space-y-5">
        {/* Upcoming Events */}
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-ambient">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-dark-slate">Upcoming Events</h3>
            <Link to="/events" className="text-xs text-slate-gray hover:text-dark-slate">See all</Link>
          </div>
          {sidebarEvents.length === 0 ? (
            <p className="text-sm text-slate-gray">No upcoming events</p>
          ) : (
            <div className="space-y-3">
              {sidebarEvents.map(event => (
                <Link key={event.event_id} to="/events" className="flex items-start space-x-3 group">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-dark-slate group-hover:underline line-clamp-1">{event.title}</p>
                    <p className="text-xs text-slate-gray">{event.date}{event.location ? ` · ${event.location}` : ''}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Recent Jobs */}
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-ambient">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-dark-slate">Recent Jobs</h3>
            <Link to="/jobs" className="text-xs text-slate-gray hover:text-dark-slate">See all</Link>
          </div>
          {sidebarJobs.length === 0 ? (
            <p className="text-sm text-slate-gray">No job postings yet</p>
          ) : (
            <div className="space-y-3">
              {sidebarJobs.map(job => (
                <Link key={job.job_id} to="/jobs" className="flex items-start space-x-3 group">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <Briefcase className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-dark-slate group-hover:underline line-clamp-1">{job.title}</p>
                    <p className="text-xs text-slate-gray">{job.company}{job.location ? ` · ${job.location}` : ''}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Quick Links */}
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-ambient">
          <h3 className="font-heading font-bold text-dark-slate mb-3">Quick Links</h3>
          <div className="space-y-2">
            {[
              { to: '/mentorship', label: 'Find a Mentor', icon: '🎓' },
              { to: '/services', label: 'Service Directory', icon: '⭐' },
              { to: '/housing', label: 'Housing', icon: '🏠' },
              { to: '/dating', label: 'Dating', icon: '❤️' },
            ].map(link => (
              <Link key={link.to} to={link.to} className="flex items-center px-3 py-2 rounded-xl hover:bg-slate-50 transition-colors text-sm text-dark-slate">
                <span className="mr-3">{link.icon}</span>
                {link.label}
                <ArrowRight className="w-3 h-3 ml-auto text-slate-gray" />
              </Link>
            ))}
          </div>
        </div>
      </aside>
      </div>
      </div>
      <ReportDialog target={reportTarget} onClose={() => setReportTarget(null)} />

      {/* All-reactors dialog — grouped by reaction type */}
      <Dialog open={!!likersDialog} onOpenChange={(open) => !open && setLikersDialog(null)}>
        <DialogContent className="max-w-sm rounded-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Reactions ({likersDialog?._likers?.length || 0})</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {(() => {
              const all = likersDialog?._likers || [];
              if (all.length === 0) return <p className="text-sm text-slate-gray text-center py-6">No reactions yet.</p>;
              // Group by reaction type — preserve REACTIONS order so layout is stable.
              return REACTIONS.map(r => {
                const group = all.filter(u => (u.reaction_type || 'love') === r.type);
                if (group.length === 0) return null;
                return (
                  <div key={r.type}>
                    <div className="flex items-center gap-2 mb-1.5 px-1">
                      <span className="text-lg" aria-hidden="true">{r.emoji}</span>
                      <span className="text-xs font-semibold text-slate-gray uppercase tracking-wide">{r.label} · {group.length}</span>
                    </div>
                    <div className="space-y-1">
                      {group.map(u => (
                        <Link
                          key={u.user_id}
                          to={`/profile/${u.user_id}`}
                          onClick={() => setLikersDialog(null)}
                          className="flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-slate-50"
                        >
                          <Avatar className="w-9 h-9">
                            <AvatarImage src={u.profile_photo} />
                            <AvatarFallback className="bg-mint-fresh text-dark-slate text-xs">{u.name?.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-dark-slate truncate">{u.name}</p>
                            {(u.title || u.institution) && <p className="text-xs text-slate-gray truncate">{[u.title, u.institution].filter(Boolean).join(' · ')}</p>}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Feed;
